import { useEffect, useState } from "react";
import { Bell, BellOff, ChevronDown, Eye, EyeOff, Pencil, ShieldCheck, ShoppingBag, Wine } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-white border border-[#6B1226]/15 rounded-xl px-4 py-3 text-base outline-none focus:border-[#6B1226]/50 transition-colors";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—");

const STATUS_LABELS = {
  en_attente: "En attente",
  confirmee: "Confirmée",
  prete: "Prête",
  recuperee: "Récupérée",
  payee: "Payée",
  annulée: "Annulée",
};

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#6B1226]/10 px-4 py-3" data-testid={testid}>
      <p className="font-eyebrow text-[0.55rem] tracking-[0.18em] uppercase font-bold text-[#C5A059]">{label}</p>
      <p className="font-semibold text-sm text-[#1C1917] mt-1 leading-snug">{value}</p>
    </div>
  );
}

export function AdminClients() {
  const [clients, setClients] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/clients").then((res) => setClients(res.data)).catch(() => setClients([]));

  useEffect(() => {
    load();
  }, []);

  const startEdit = (c) => {
    const parts = (c.name || "").split(" ");
    setEditForm({
      firstName: c.first_name || parts[0] || "",
      lastName: c.last_name || parts.slice(1).join(" ") || "",
      email: c.email || "",
      phone: c.phone || "",
      password: "",
    });
    setError("");
    setShowPw(false);
  };

  const save = async (c) => {
    setBusy(true);
    setError("");
    try {
      await api.put(`/admin/users/${c.id}`, {
        first_name: editForm.firstName,
        last_name: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        password: editForm.password || undefined,
      });
      toast.success("Fiche client mise à jour");
      setEditForm(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const promote = async (c) => {
    try {
      await api.patch(`/admin/users/${c.id}`, { role: "admin" });
      toast.success(`${c.name} est maintenant administrateur — visible dans l'onglet Équipe`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-clients">
      <p className="font-display text-xl font-semibold text-[#1C1917] mb-1">Les clients de la cave</p>
      <p className="text-sm text-[#57534E] mb-4">
        Touchez un client pour voir sa fiche complète : informations modifiables, commandes, statistiques de visite et notifications.
      </p>
      {clients === null ? (
        <p className="text-sm text-[#57534E] italic">Chargement…</p>
      ) : clients.length === 0 ? (
        <p className="text-sm text-[#78716C] italic" data-testid="admin-clients-empty">Aucun client inscrit pour l'instant.</p>
      ) : (
        <ul className="space-y-2.5" data-testid="admin-clients-list">
          {clients.map((c) => {
            const s = c.stats || {};
            const open = openId === c.id;
            return (
              <li key={c.id} className="bg-white rounded-2xl border border-[#6B1226]/10 p-4" data-testid="admin-client-row">
                <button onClick={() => setOpenId(open ? null : c.id)} className="w-full flex items-center gap-3 text-left" data-testid="admin-client-toggle">
                  <span className="w-11 h-11 rounded-full bg-[#F5EEDC] text-[#8C6D23] flex items-center justify-center font-display text-lg font-semibold shrink-0">
                    {(c.first_name || c.name || " ").charAt(0).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-sm text-[#1C1917] truncate">{c.name}</span>
                    <span className="block text-xs text-[#78716C] truncate">{c.email}</span>
                    <span className="block text-[0.65rem] text-[#A8A29E] mt-0.5">
                      {s.orders_count || 0} commande{(s.orders_count || 0) > 1 ? "s" : ""} · {eur(s.total_spent || 0)}
                      {s.last_seen_rel ? ` · vu ${s.last_seen_rel}` : ""}
                    </span>
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-wide shrink-0 ${
                      s.push_subscribed ? "bg-[#3F6212]/10 text-[#3F6212]" : "bg-stone-100 text-stone-400"
                    }`}
                    title={s.push_subscribed ? "Inscrit aux notifications" : "Non inscrit aux notifications"}
                    data-testid="admin-client-push-badge"
                  >
                    {s.push_subscribed ? <Bell size={11} /> : <BellOff size={11} />}
                    {s.push_subscribed ? "Notif." : "Sans notif."}
                  </span>
                  <ChevronDown size={16} className={`text-[#C5A059] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <div className="mt-4 pt-4 border-t border-[#6B1226]/10 space-y-4" data-testid="admin-client-detail">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <Stat label="Commandes" value={s.orders_count || 0} testid="client-stat-orders" />
                      <Stat label="Montant total" value={eur(s.total_spent || 0)} testid="client-stat-spent" />
                      <Stat label="Bouteilles" value={s.bottles || 0} testid="client-stat-bottles" />
                      <Stat label="Dernière connexion" value={s.last_seen_rel || "jamais"} testid="client-stat-last-seen" />
                      <Stat label="Dernière commande" value={s.last_order_at ? fmtDate(s.last_order_at) : "jamais"} testid="client-stat-last-order" />
                      <Stat label="Maison préférée" value={s.favorite_producer || "—"} testid="client-stat-favorite" />
                    </div>

                    {(Object.keys(s.visit_producers || {}).length > 0 || s.last_wine_visited) && (
                      <div className="bg-[#F5EEDC]/60 border border-[#C5A059]/30 rounded-2xl p-4" data-testid="client-visits">
                        <p className="font-eyebrow text-[0.55rem] tracking-[0.18em] uppercase font-bold text-[#8C6D23] mb-2 flex items-center gap-1.5">
                          <Wine size={11} /> Maisons visitées
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(s.visit_producers || {}).map(([p, n]) => (
                            <span key={p} className="rounded-full bg-white border border-[#C5A059]/40 text-[#8C6D23] px-3 py-1 text-[0.65rem] font-semibold" data-testid="client-visit-producer">
                              {p} · {n} visite{n > 1 ? "s" : ""}
                            </span>
                          ))}
                        </div>
                        {s.last_wine_visited && (
                          <p className="text-[0.65rem] text-[#78716C] mt-2">
                            Dernière fiche vue : {s.last_wine_visited.name} ({fmtDate(s.last_wine_visited.at)})
                          </p>
                        )}
                      </div>
                    )}

                    <div className="text-xs text-[#57534E] space-y-1" data-testid="client-infos">
                      <p><span className="font-semibold text-[#1C1917]">Téléphone :</span> {c.phone || "—"}</p>
                      <p><span className="font-semibold text-[#1C1917]">Adresse :</span> {[c.address_line1, c.address_line2, c.postal_code, c.city].filter(Boolean).join(", ") || "—"}</p>
                      <p><span className="font-semibold text-[#1C1917]">Né(e) le :</span> {c.birth_date ? fmtDate(c.birth_date) : "—"}</p>
                      <p><span className="font-semibold text-[#1C1917]">Créneau habituel :</span> {c.pickup_pref || "—"}{c.pickup_note ? ` — « ${c.pickup_note} »` : ""}</p>
                      <p><span className="font-semibold text-[#1C1917]">Inscrit le :</span> {fmtDate(c.created_at)}</p>
                      <p>
                        <span className="font-semibold text-[#1C1917]">Notifications push :</span>{" "}
                        {s.push_subscribed ? "inscrit — reçoit les annonces" : "non inscrit"}
                      </p>
                    </div>

                    {s.recent_orders?.length > 0 && (
                      <div data-testid="client-orders">
                        <p className="font-eyebrow text-[0.55rem] tracking-[0.18em] uppercase font-bold text-[#C5A059] mb-2 flex items-center gap-1.5">
                          <ShoppingBag size={11} /> Dernières commandes
                        </p>
                        <ul className="space-y-1.5">
                          {s.recent_orders.map((o) => (
                            <li key={o.id} className="flex items-center justify-between bg-[#FAF6F0] rounded-xl px-3.5 py-2.5 text-xs" data-testid="client-order-row">
                              <span className="font-semibold text-[#1C1917]">N° {o.number}</span>
                              <span className="text-[#78716C]">{fmtDate(o.created_at)}</span>
                              <span className="text-[#6B1226] font-semibold">{STATUS_LABELS[o.status] || o.status}</span>
                              <span className="font-bold text-[#1C1917]">{eur(o.total)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {editForm ? (
                      <div className="space-y-3" data-testid="client-edit-form">
                        <div className="grid grid-cols-2 gap-2.5">
                          <input value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} placeholder="Prénom" className={inputCls} data-testid="client-edit-firstname" />
                          <input value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} placeholder="Nom" className={inputCls} data-testid="client-edit-lastname" />
                        </div>
                        <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="client-edit-email" />
                        <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Téléphone" className={inputCls} data-testid="client-edit-phone" />
                        <div className="relative">
                          <input type={showPw ? "text" : "password"} minLength={6} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Nouveau mot de passe (vide = inchangé)" className={`${inputCls} pr-11`} data-testid="client-edit-password" />
                          <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B1226]/50 hover:text-[#6B1226]" data-testid="client-edit-password-toggle" aria-label="Afficher le mot de passe">
                            {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                          </button>
                        </div>
                        {error && (
                          <p className="text-sm text-[#6B1226] bg-[#6B1226]/5 border border-[#6B1226]/20 rounded-xl px-4 py-3" data-testid="client-edit-error">{error}</p>
                        )}
                        <div className="flex gap-2">
                          <button onClick={() => setEditForm(null)} className="flex-1 rounded-full border border-[#6B1226]/25 text-[#6B1226] py-3 text-sm font-bold hover:bg-[#6B1226]/5 transition-colors" data-testid="client-edit-cancel">
                            Annuler
                          </button>
                          <button
                            onClick={() => save(c)}
                            disabled={busy || !editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()}
                            className="flex-1 rounded-full bg-[#6B1226] text-[#FAF6F0] py-3 text-sm font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60"
                            data-testid="client-edit-save"
                          >
                            {busy ? "Enregistrement…" : "Enregistrer"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => startEdit(c)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#6B1226]/25 text-[#6B1226] px-4 py-2 text-xs font-bold hover:bg-[#6B1226]/5 transition-colors"
                          data-testid="client-edit-open"
                        >
                          <Pencil size={13} /> Modifier la fiche
                        </button>
                        <button
                          onClick={() => promote(c)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-[#C5A059]/60 text-[#8C6D23] px-4 py-2 text-xs font-bold hover:bg-[#F5EEDC] transition-colors"
                          data-testid="client-promote"
                        >
                          <ShieldCheck size={13} /> Passer administrateur
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
